import type { makeFetchTransport } from '@sentry/browser';
import type { Envelope } from '@sentry/core';
import type { Transport, TransportMakeRequestResponse } from '@sentry/core';

export type CircuitBreakerTransportOptions = {
  /** Consecutive failed sends before the circuit opens. */
  failureThreshold: number;
  /** Called once when the circuit opens (e.g. `Sentry.close()`). */
  onCircuitOpen?: () => void;
};

export type FetchTransportFactory = typeof makeFetchTransport;

/**
 * Wraps a Sentry fetch transport factory with a circuit breaker.
 * After {@link CircuitBreakerTransportOptions.failureThreshold} consecutive
 * failed sends, further envelopes are dropped without network I/O.
 *
 * @returns The result of this operation.
 * @param baseFactory The Base factory value.
 * @param options The Options value.
 */
export function createCircuitBreakerTransport(
  baseFactory: FetchTransportFactory,
  options: CircuitBreakerTransportOptions,
): FetchTransportFactory {
  return (transportOptions, nativeFetch) => {
    const baseTransport = baseFactory(transportOptions, nativeFetch);
    let consecutiveFailures = 0;
    let circuitOpen = false;
    let circuitOpenNotified = false;

    const notifyCircuitOpen = (): void => {
      if (circuitOpenNotified) {
        return;
      }

      circuitOpenNotified = true;
      options.onCircuitOpen?.();
    };

    const openCircuitIfNeeded = (): void => {
      if (circuitOpen || consecutiveFailures < options.failureThreshold) {
        return;
      }

      circuitOpen = true;
      notifyCircuitOpen();
    };

    const recordFailure = (): void => {
      consecutiveFailures++;
      openCircuitIfNeeded();
    };

    const recordSuccess = (): void => {
      consecutiveFailures = 0;
    };

    const wrappedTransport: Transport = {
      send(envelope: Envelope): PromiseLike<TransportMakeRequestResponse> {
        if (circuitOpen) {
          return Promise.resolve({});
        }

        return Promise.resolve(baseTransport.send(envelope)).then(
          (response) => {
            if (isTransportFailure(response)) {
              recordFailure();
            } else {
              recordSuccess();
            }

            return response;
          },
          (error: unknown) => {
            recordFailure();
            throw error;
          },
        );
      },

      flush(timeout?: number): PromiseLike<boolean> {
        if (circuitOpen) {
          return Promise.resolve(true);
        }

        return baseTransport.flush(timeout);
      },
    };

    return wrappedTransport;
  };
}

/**
 *
 * @returns The result of this operation.
 * @param response The Response value.
 */
function isTransportFailure(response: TransportMakeRequestResponse): boolean {
  const statusCode = response.statusCode;
  if (statusCode === undefined || statusCode === 0) {
    return true;
  }

  return statusCode >= 400;
}
